
import AppMobileFullScreenContainer from "@/components/common/MobileFullScreenContainer.vue";
import AppToolbar from "@/components/common/ToolbarBase.vue";
import {Contact, Product, ProductStock} from "@/models/base";
import { ProductService } from "@/services/product-service";
import { SizeTableService } from "@/services/size-table-service";
import { AuthService } from "@/services";
import { MODULE } from "@/config/modules";
import Vue from "vue";
import AppTab1 from "./ProductEditDefault.vue";
import AppTab2 from "./ProductEditBuy.vue";
import AppTab4 from "./ProductEditExtraFields.vue";
import AppTab3 from "./ProductEditStock.vue";
import AppTab5 from "./ProductEditTaxes.vue";
import AppTab6 from "./ProductEditAwsDocuments.vue";
import AppTab7 from "./ProductEditPriceRules.vue";
import AppTab8 from "./ProductEditLabels.vue";
import { UserConfigService } from "../../services/user-config-service";
import { DefaultEditTab, getVisibleDefaultEditTabs } from "@/utils/tabs/default-edit-tabs";
import { ConfirmResult, ISelectItem } from "winfakt-vue-components";
import { DossierConfigService } from "@/services";
import {ShopService} from "@/services/shop-service";
import { PriceListProductService } from "@/services/price-list-product-service";
import {ProductSupplier} from "@/utils/product-supplier";
import AppCustomViewTabFrames from "@/components/browse/CustomViewTabFrames.vue";
import AppExtraFieldTab from "@/components/ExtraFieldTab.vue";
import { ExtraFieldTabConfig } from "@/models/dossier-configs/extra-field-tab-config";
import { ExtraFieldNamesConfig } from "@/models/dossier-configs/extra-field-names-config";
import { HttpError } from "@/utils/services/http-error";

export default Vue.extend({
	data() {
		return {
			tab: 0,
			pendingChanges: false,
			lastProductState: "{}",
			autoUpdateProductComposition: false,
			generalSettings: UserConfigService.getGeneralSettings(),
			autoBarcode: "",
			barCodeExists: false,
			isSaving: false,
			priceListCheckedSku: null as string | null
		};
	},
	props: {
		value: {
			type: Object as () => Product,
			required: true,
		},
		showEditComposition: Boolean,
		drawerButtonDisabled: Boolean,
		isInfo: Boolean,
		skuDisabled: Boolean
	},
	computed: {
		buttonBarItems():ISelectItem<string>[]{
			let result:ISelectItem<string>[] =  [
				{
					text: this.$ct("edit-composition"),
					value: "editComposition"},
				{
					text: this.$ct("load-last-opened"),
					value: "loadLastOpenedItem",
					disabled: this.lastLoadedDisabled
				}
			];
			return result;
		},
		currentTab(): string {
			return this.tabs[this.tab];
		},
		defaultTabs(): DefaultEditTab[] {
			return getVisibleDefaultEditTabs(4);
		},
		currentTabKey(): string {
			return this.defaultTabs[this.tab]?.key || "";
		},
		tabs(): string[] {
			let t = this.defaultTabs.map(t=>t.label());
			let e = UserConfigService.getCustomViewTabs();
			let v = e.getTabNamesForBrowse(4);
			return [...t, ...v, ...this.extraFieldNamesConfig.getTabNames()];
		},
		customViewTabs(){
			return UserConfigService.getCustomViewTabsForBrowse(4);
		},
		extraFieldNamesConfig():ExtraFieldNamesConfig {
			return DossierConfigService.getExtraFieldNamesConfigs().ProductNames;
		},
		extraFieldTabs():ExtraFieldTabConfig[] {
			return this.extraFieldNamesConfig.getTabs();
		},
		extraFieldTabStart():number {
			return this.defaultTabs.length + this.customViewTabs.length;
		},
		product(): Product {
			return this.value;
		},
		titleKey():string {
			return this.value.ID == 0 ? "title-new" : "title-edit";
		},
		hasPriceListModule():boolean{
			return AuthService.hasModule(MODULE.EXTERNE_PRODUCT_CATALOGUS);
		},
		lastLoadedDisabled():boolean{
			if (this.value.ID != 0){
				return true;
			}
			return !ProductService.lastOpenedItem.hasStoredItem();
		},
	},
	watch: {
		product: {
			immediate: true,
			deep: true,
			handler(product: Product, oldProduct: Product) {
				if (oldProduct != product) {
					this.lastProductState = JSON.stringify(product.getJSON());
					return;
				}
				if (JSON.stringify(product.getJSON()) == this.lastProductState) return;
				this.pendingChanges = true;
				if (this.product.ID == 0 && this.product.Stock == null){
					this.product.Stock = new ProductStock();
				}
			}
		},
	},
	methods: {
		onUpdateParent(event:Contact){
			this.$emit("input", new Product(event));
		},
		calculateEan13CheckDigit(ean13Code:string) {
			const weights = [1, 3, 1, 3, 1, 3, 1, 3, 1, 3, 1, 3, 1];
			let sum = 0;
			for (let i = 0; i < 12; i++) {
				sum += parseInt(ean13Code[i]) * weights[i];
			}
			const checkDigit = (10 - (sum % 10)) % 10;
			return checkDigit;
		},
		createEan13Barcode(barcode:number):string {
			let barcodeString = `${barcode}`;
			if (barcodeString.length >= 13) return barcodeString;
			if (12 - barcodeString.length <= 0) return `${barcodeString}${this.calculateEan13CheckDigit(barcodeString)}`;
			barcodeString = `${"0".repeat(12 - barcodeString.length)}${barcode}`;
			return `${barcodeString}${this.calculateEan13CheckDigit(barcodeString)}`;
		},
		setAutoBarcode(){
			if (this.product.ID != 0) return;
			let autoBarcodeConfig = DossierConfigService.getAutoBarcodeConfig();
			if (!autoBarcodeConfig.Enabled) return;
			let barcode = autoBarcodeConfig.NextBarcode;
			let barcodeString = this.createEan13Barcode(barcode);
			this.autoBarcode = barcodeString;
			this.product.BarCode = barcodeString;
		},
		async incremenetAutoBarcode(){
			let autoBarcodeConfig = DossierConfigService.getAutoBarcodeConfig();
			if (!autoBarcodeConfig.Enabled)return;
			let autoBarcode = this.createEan13Barcode(autoBarcodeConfig.NextBarcode);
			if (this.autoBarcode != autoBarcode) return;
			if (this.product.BarCode != this.autoBarcode) return;
			await ProductService.incrementAutoBarcode();
		},
		async askToSave(): Promise<boolean> {
			let cB = this.$confirmBuilder();
			cB.setTitle(this.$ct("common.attention"));
			cB.setClass("warning");
			cB.setMessage(this.$ct("save-required"));
			return (await cB.show()) == ConfirmResult.YES;
		},
		// eslint-disable-next-line max-lines-per-function
		async save(returnToView:boolean = false):Promise<boolean> {
			if (this.isSaving) return false;
			this.isSaving = true;
			try{

				if (!this.product.Sku && !this.generalSettings.AutoAssignProductSku) {
					this.$wf.notify(this.$ct("error-sku-empty").toString(), "danger", 3500, "exclamation-triangle");
					return false;
				}

				if (!await this.beforeSave()) {
					return false;
				}

				if (this.product.ID != 0 && this.product.ID != null) {
					var product = await ProductService.putProduct(this.product);
				} else {
					var product = await ProductService.postProduct(this.product);
					await this.incremenetAutoBarcode();
				}

				// Per-size barcodes live in their own table, so persist them after the product
				// save (we need the new product ID for freshly created products).
				if (AuthService.hasModule(MODULE.MATENTABEL) && this.product.SizeTableID) {
					await SizeTableService.saveProductSizeBarcodes(product.ID, this.product.SizeBarcodes);
				}

				if(DossierConfigService.getGeneralConfig().AutoShopSynch){
					await ShopService.product(product.ID);
				}

				if(this.generalSettings.AutoUpdateProductComposition){
					await ProductService.updateProductCompositions(product.ID);
				}

				this.$emit("input", product);
				this.pendingChanges = false;
				this.$wf.notify(this.$ct("save-success").toString(), "success", 3000, "check");
				this.$emit("clearChanges");
				this.$emit("saved", product, returnToView);

				return true;
			}catch(err){

			}finally{
				this.isSaving = false;
			}
			return false;
		},
		async beforeSave() {
			if (!this.product.ID && !this.generalSettings.AutoAssignProductSku) {
				if(!await this.checkIfSkuIsFree()){
					return false;
				}
			}
			return true;
		},
		async checkIfSkuIsFree(){
			let exists = await ProductService.existsBySkuIncludeNonActive(this.product.Sku + "");
			if (exists) {
				this.$wf.notify(this.$ct("error-sku-exists").toString(), "danger", 3500, "exclamation-triangle");
				return false;
			}
			await this.checkPriceList();
			return true;
		},
		async checkPriceList(){
			if (!this.hasPriceListModule || this.product.PriceListProductID || !this.product.Sku) {
				return;
			}
			let sku = this.product.Sku + "";
			if (this.priceListCheckedSku == sku) {
				return;
			}
			this.priceListCheckedSku = sku;
			let plProd = await PriceListProductService.findBySku(sku);
			if (!plProd?.ID) {
				return;
			}
			let builder = this.$confirmBuilder(this.$ct("found-in-pricelist"));
			builder.setTitle(this.$t("common.info"));
			if (!await builder.show()) {
				return;
			}
			let imported = await PriceListProductService.toProduct(plProd);
			imported.Active = true;
			if (imported.ExtraFields){
				DossierConfigService.getExtraFieldNamesConfigs().ProspectNames.setDefaultValues(imported.ExtraFields);
			}
			this.$emit("input", imported);
		},
		async editComposition() {
			if (this.pendingChanges || this.product.ID == 0) {
				let result = await this.askToSave();
				if (!result) {
					return;
				}
				if (!await this.save()) {
					return;
				}
			}
			this.$emit("clearChanges");
			this.$router.push(`/products/composition/${this.product.ID}`);
		},
		trigger(value: string) {
			(this as any)[value]();
		},
		async cancel() {
			this.$emit("cancel");
		},
		async remove() {
			let confirm = await this.$wf.confirm(this.$ct("confirm-delete"), this.$ct("common.yes"), this.$ct("common.no"), this.$ct("common.warning"), "warning");
			if (!confirm) return;
			await ProductService.delProducts([this.value]);
			this.$emit("delete");
		},
		async loadLastOpenedItem(){
			let product = new Product(ProductService.lastOpenedItem.getLastOpenedItem());

			product.ID = 0;
			product.Sku = null;
			product.BarCode = null;
			product.Suppliers = product.Suppliers.map((s:ProductSupplier) => {
				s.SupplierCode = ""; s.ManufacturerCode = ""; return s;
			});

			if (product.Stock) {
				product.Stock.EmtpyForLoadFromLastOpenedProduct();
			}

			let copyImages = false;
			if (product.Images.length > 0){
				let result = await this.$wf.confirm(this.$ct("keep-added-images"), this.$ct("common.yes"), this.$ct("common.no"), this.$ct("common.warning"), "warning");
				copyImages = result == ConfirmResult.YES;
			}
			if (copyImages){
				await this.copyImagesToUpload(product);
			}
			product.Images = [];
			product.CoverImageID = null;

			this.$emit("input", product);
		},
		async copyImagesToUpload(product:Product){
			for (const image of product.Images) {
				const imageUrl = image.getFileUrl();
				if (!imageUrl) continue;
				try {
					const response = await fetch(imageUrl);
					const blob = await response.blob();
					const fileName = imageUrl.split("/").pop() || "image.jpg";
					const file = new File([blob], fileName, { type: blob.type });
					product.toUploadImages.push({
						file,
						setAsCover: image.setAsCover,
						url: ""
					});
				} catch (err) {
					console.error("Failed to copy image:", err);
				}
			}
		},
		async checkIfBarCodeExists(){
			if (!this.product.BarCode) {
				this.barCodeExists = false;
				return;
			}
			try{
				await ProductService.getByBarCode(this.product.BarCode, {excludedProductId: this.product.ID});
				if (this.product.BarCode == this.autoBarcode){
					await this.incremenetAutoBarcode();
					this.setAutoBarcode();
					await this.checkIfBarCodeExists();
				}else{
					this.barCodeExists = true;
				}
			}catch(err){
				let error = err as HttpError;
				if (error.key == "ELEMENT_NOT_FOUND"){
					this.barCodeExists = false;
					error.dontShow = true;
				}
			}
		}

	},
	created(){
		// There is already a watcher with deep settings enabled so this one is without the deep setting.
		this.$watch("product", this.setAutoBarcode, {immediate: true, deep: false});
	},
	components: {
		AppTab1,
		AppTab2,
		AppTab3,
		AppTab4,
		AppTab5,
		AppTab6,
		AppTab7,
		AppTab8,
		AppToolbar,
		AppMobileFullScreenContainer,
		AppCustomViewTabFrames,
		AppExtraFieldTab
	},
	i18nEx: {
		componentPrefix: "components.products"
	}
});
